/**
 * The numbers behind the Missions glance strip: what waits on you, what runs, how much of the
 * backlog is done, and how many items became Done day by day. Pure, so every figure is tested.
 */
import { REVIEW_ATTEMPTS } from '@orgus/protocol';
import type { BacklogItem, Id, ItemReviewMode, MissionState } from '@orgus/protocol';
import type { WorkState } from '@orgus/ui';
import { finishedAt, reviewQueue } from './model.ts';
import type { MissionRow } from './model.ts';

const DAY = 24 * 60 * 60_000;

/** The work state each mission state draws with (status.md, "Work states"). */
export const MISSION_WORK_STATE: Record<MissionState, WorkState> = {
  waiting: 'needs-you',
  failed: 'failed',
  running: 'running',
  planned: 'queued',
  done: 'landed',
  stopped: 'stale',
};

/** Blocked: a decided approval (expired or denied) stopped it, so it waits on a person to unblock it. */
export function isBlocked(r: Pick<MissionRow, 'view'>): boolean {
  return r.view.state === 'waiting' && r.view.blockers.some((b) => b.kind === 'approval' && !!b.outcome && b.phase !== 'resuming');
}

export interface MissionsGlance {
  waiting: number;
  blocked: number;
  running: number;
  planned: number;
  failed: number;
  done: number;
  stopped: number;
  /** Planned, running and waiting. */
  active: number;
  /** Story points done and in the backlog (dropped work left out). Items when nothing is estimated. */
  backlog: { value: number; of: number; unit: 'points' | 'items' };
  /** Items that became Done per day over the last `days`, oldest first (ORGU-85: Done items, not missions). */
  finishedByDay: number[];
  finishedThisWeek: number;
  /** Items Done in all (epics, features and dropped work left out). */
  doneItems: number;
  /** Finished missions whose items wait In review (the Review tab). */
  review: number;
  /** Of those, the ones a person reviews (Review People, or Orgus could not): they count in Needs you. */
  reviewByPeople: number;
  /** Open AI proposals and suggested defects: the Review tab lists them beside the items, so In review counts them too. */
  proposals: number;
  /** When the oldest of them finished; null with none. */
  oldestReviewAt: number | null;
  /** The mission that has waited on a person the longest: where Answer goes. */
  firstWaitingId: Id | null;
}

/**
 * Does this finished item wait for a person to review it? Yes when its project's Review is People
 * (or unknown, from an older hub), or when Orgus gave up reviewing it (ORGU-85).
 */
export function waitsForPeople(r: Pick<MissionRow, 'mission' | 'item'>, review: Readonly<Record<Id, ItemReviewMode>>): boolean {
  if (review[r.mission.projectId] !== 'orgus') return true;
  const rv = r.item?.review;
  return !!rv && rv.missionId === r.mission.id && rv.state === 'error' && rv.attempts >= REVIEW_ATTEMPTS;
}

/** When an item became done: kept from ORGU-85 on; older items fall back to their last status note or change. */
export function doneAtOf(i: BacklogItem): number {
  return i.doneAt ?? i.statusNote?.at ?? i.updatedAt;
}

/** What the Review tab needs of a proposal or a suggested defect. */
type ReviewDraft = { status: string; projectId: Id };

/** Open AI proposals and suggested defects in the project (all projects when null): the Review tab's second list. */
export function openProposals(proposals: readonly ReviewDraft[] = [], signals: readonly ReviewDraft[] = [], projectId: Id | null): number {
  const open = (x: ReviewDraft) => x.status === 'open' && (!projectId || x.projectId === projectId);
  return proposals.filter(open).length + signals.filter(open).length;
}

/**
 * QA-17: of the missions that wait on a person, how many the Inbox does not list. The Inbox holds
 * what the shared Needs you selector counts (spec, "needs you": blocked and asked sessions); a
 * question older than a day, one set aside, or one Orgus answers itself is left out by design, so
 * the Missions tile says where those are answered instead of disagreeing with the Inbox badge.
 */
export function waitingOutsideInbox(rows: readonly Pick<MissionRow, 'mission' | 'view'>[], bySession: ReadonlyMap<Id, string>, projectId: Id | null): number {
  const counted = (id: Id) => {
    const l = bySession.get(id);
    return l === 'blocked' || l === 'asked';
  };
  return rows.filter((r) => (!projectId || r.mission.projectId === projectId) && r.view.state === 'waiting' && !r.mission.sessionIds.some(counted)).length;
}

export function missionsGlance(
  rows: readonly MissionRow[],
  items: readonly BacklogItem[],
  projectId: Id | null,
  now: number,
  days = 14,
  review: Readonly<Record<Id, ItemReviewMode>> = {},
  ai: { proposals?: readonly ReviewDraft[]; signals?: readonly ReviewDraft[] } = {},
): MissionsGlance {
  const mine = rows.filter((r) => !projectId || r.mission.projectId === projectId);
  const count = (s: MissionState) => mine.filter((r) => r.view.state === s).length;
  const g: MissionsGlance = {
    waiting: count('waiting'),
    blocked: mine.filter(isBlocked).length,
    running: count('running'),
    planned: count('planned'),
    failed: count('failed'),
    done: count('done'),
    stopped: count('stopped'),
    active: 0,
    backlog: { value: 0, of: 0, unit: 'points' },
    finishedByDay: Array.from({ length: days }, () => 0),
    finishedThisWeek: 0,
    doneItems: 0,
    review: 0,
    reviewByPeople: 0,
    proposals: openProposals(ai.proposals, ai.signals, projectId),
    oldestReviewAt: null,
    firstWaitingId: null,
  };
  g.active = g.waiting + g.running + g.planned;

  const work = items.filter((i) => (!projectId || i.projectId === projectId) && i.status !== 'dropped' && i.kind !== 'epic' && i.kind !== 'feature');
  const estimated = work.filter((i) => i.estimate != null && i.estimate > 0);
  g.backlog = estimated.length
    ? { value: estimated.filter((i) => i.status === 'done').reduce((n, i) => n + i.estimate!, 0), of: estimated.reduce((n, i) => n + i.estimate!, 0), unit: 'points' }
    : { value: work.filter((i) => i.status === 'done').length, of: work.length, unit: 'items' };

  const queue = reviewQueue(mine);
  g.review = queue.length;
  g.reviewByPeople = queue.filter((r) => waitsForPeople(r, review)).length;
  g.oldestReviewAt = queue[0] ? finishedAt(queue[0].mission) : null;
  // Answerable first (a blocked one needs a decision too, but its question is already decided).
  const waiting = mine.filter((r) => r.view.state === 'waiting').sort((a, b) => Number(isBlocked(a)) - Number(isBlocked(b)) || a.mission.updatedAt - b.mission.updatedAt);
  g.firstWaitingId = waiting[0]?.mission.id ?? null;

  const today = Math.floor(now / DAY);
  for (const i of work) {
    if (i.status !== 'done') continue;
    g.doneItems += 1;
    const ago = today - Math.floor(doneAtOf(i) / DAY);
    if (ago < 0 || ago >= days) continue;
    g.finishedByDay[days - 1 - ago]! += 1;
    if (ago < 7) g.finishedThisWeek += 1;
  }
  return g;
}
